import React, { useState, useEffect } from 'react';
import { UserPlus, Users, ArrowLeft, Check, AlertCircle, Eye, EyeOff, Loader, Key, Sprout } from 'lucide-react';
import { Button, GardenBackground } from '../components/ui';

export const JoinExistingFamily = ({ onNavigate, onJoinSuccess, inviteCodeParam = '' }) => {
  const [step, setStep] = useState(1);
  const [inviteCode, setInviteCode] = useState(inviteCodeParam);
  const [familyInfo, setFamilyInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

  const [accountData, setAccountData] = useState({
    username: '',
    pin: '',
    confirmPin: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (inviteCodeParam) {
      validateInviteCode(inviteCodeParam);
    }
  }, [inviteCodeParam]);

  const validateInviteCode = async (code) => {
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-character invite code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/families/invite/${code.toUpperCase()}`);
      const result = await response.json();

      if (result.success && result.data.valid) {
        setFamilyInfo(result.data);
        setStep(2);
        setError('');
      } else {
        setError(result.data?.error || 'Invalid or expired invite code');
        setFamilyInfo(null);
      }
    } catch (error) {
      setError('Unable to validate invite code. Please try again.');
      setFamilyInfo(null);
    }

    setLoading(false);
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    validateInviteCode(inviteCode);
  };

  const validateAccountData = () => {
    const errors = {};

    if (familyInfo.inviteType === 'child') {
      if (!accountData.username.trim()) {
        errors.username = 'Username is required';
      } else if (accountData.username.length < 3) {
        errors.username = 'Username must be at least 3 characters';
      }
      if (!accountData.pin) {
        errors.pin = 'PIN is required';
      } else if (accountData.pin.length !== 4 || !/^\d+$/.test(accountData.pin)) {
        errors.pin = 'PIN must be exactly 4 digits';
      }
      if (accountData.pin !== accountData.confirmPin) {
        errors.confirmPin = 'PINs do not match';
      }
    } else {
      if (!accountData.firstName.trim()) errors.firstName = 'First name is required';
      if (!accountData.lastName.trim()) errors.lastName = 'Last name is required';
      if (!accountData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(accountData.email)) {
        errors.email = 'Please enter a valid email address';
      }
      if (!accountData.password) {
        errors.password = 'Password is required';
      } else if (accountData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      if (accountData.password !== accountData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleJoinFamily = async (e) => {
    e.preventDefault();

    if (!validateAccountData()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/families/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inviteCode: inviteCode.toUpperCase(),
          accountData,
          accountType: familyInfo.inviteType
        })
      });

      const result = await response.json();

      if (result.success) {
        if (onJoinSuccess) onJoinSuccess(result.data);
      } else {
        setError(result.error || 'Failed to join family');
      }
    } catch (error) {
      setError('Unable to join family. Please try again.');
    }

    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setAccountData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatExpiryTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const days = Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24));
    const hours = Math.ceil((date - new Date()) / (1000 * 60 * 60));

    if (days >= 1) {
      return days === 1 ? 'Expires in 1 day' : `Expires in ${days} days`;
    } else {
      return hours === 1 ? 'Expires in 1 hour' : `Expires in ${hours} hours`;
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <GardenBackground />
      <div className="w-full max-w-sm relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl text-gray-900 mb-1" style={{ fontFamily: "'DM Serif Display', serif" }}>Join a Family</h1>
            <p className="text-sm text-gray-500">
              {step === 1
                ? 'Enter your invite code to get started'
                : familyInfo?.inviteType === 'child'
                  ? 'Create your account'
                  : 'Create your guardian account'
              }
            </p>
          </div>

          {/* Step 1: Enter Invite Code */}
          {step === 1 && (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Family Invite Code
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="ABC123"
                    maxLength={6}
                    className="w-full px-3 py-3 text-center text-base font-mono tracking-widest border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors bg-gray-50 text-sm"
                    disabled={loading}
                  />
                  <Key className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-300" />
                </div>
                {error && (
                  <div className="mt-2 flex items-center text-red-500 text-sm">
                    <AlertCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                    {error}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || inviteCode.length !== 6}
                size="lg"
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>

              <button
                type="button"
                className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center justify-center gap-1"
                onClick={() => onNavigate('welcome')}
                disabled={loading}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
            </form>
          )}

          {/* Step 2: Create Account */}
          {step === 2 && familyInfo && (
            <div className="space-y-4">
              {/* Family info banner */}
              <div className="p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2">
                  {familyInfo.inviteType === 'child' ? (
                    <Sprout className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Users className="w-4 h-4 text-blue-600" />
                  )}
                  <span className="text-sm font-medium text-gray-900">
                    {familyInfo.familyName}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {familyInfo.inviteType === 'child' ? 'Child' : 'Guardian'} account
                  {familyInfo.expiresAt && ` · ${formatExpiryTime(familyInfo.expiresAt)}`}
                </p>
              </div>

              <form onSubmit={handleJoinFamily} className="space-y-4">
                {familyInfo.inviteType === 'child' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
                      <input
                        type="text"
                        value={accountData.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                          fieldErrors.username ? 'border-red-300' : 'border-gray-200'
                        }`}
                        placeholder="Pick a username"
                        disabled={loading}
                      />
                      {fieldErrors.username && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.username}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">PIN (4 digits)</label>
                      <input
                        type="password"
                        value={accountData.pin}
                        onChange={(e) => handleInputChange('pin', e.target.value)}
                        maxLength={4}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm text-center font-mono tracking-widest focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                          fieldErrors.pin ? 'border-red-300' : 'border-gray-200'
                        }`}
                        placeholder="- - - -"
                        disabled={loading}
                      />
                      {fieldErrors.pin && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.pin}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm PIN</label>
                      <input
                        type="password"
                        value={accountData.confirmPin}
                        onChange={(e) => handleInputChange('confirmPin', e.target.value)}
                        maxLength={4}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm text-center font-mono tracking-widest focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                          fieldErrors.confirmPin ? 'border-red-300' : 'border-gray-200'
                        }`}
                        placeholder="- - - -"
                        disabled={loading}
                      />
                      {fieldErrors.confirmPin && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.confirmPin}</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                        <input
                          type="text"
                          value={accountData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                            fieldErrors.firstName ? 'border-red-300' : 'border-gray-200'
                          }`}
                          placeholder="Sarah"
                          disabled={loading}
                        />
                        {fieldErrors.firstName && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors.firstName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                        <input
                          type="text"
                          value={accountData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                            fieldErrors.lastName ? 'border-red-300' : 'border-gray-200'
                          }`}
                          placeholder="Johnson"
                          disabled={loading}
                        />
                        {fieldErrors.lastName && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <input
                        type="email"
                        value={accountData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                          fieldErrors.email ? 'border-red-300' : 'border-gray-200'
                        }`}
                        placeholder="sarah@example.com"
                        disabled={loading}
                      />
                      {fieldErrors.email && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={accountData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          className={`w-full px-3 py-2.5 pr-9 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                            fieldErrors.password ? 'border-red-300' : 'border-gray-200'
                          }`}
                          placeholder="At least 6 characters"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          disabled={loading}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {fieldErrors.password && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.password}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                      <input
                        type="password"
                        value={accountData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-colors ${
                          fieldErrors.confirmPassword ? 'border-red-300' : 'border-gray-200'
                        }`}
                        placeholder="Re-enter password"
                        disabled={loading}
                      />
                      {fieldErrors.confirmPassword && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.confirmPassword}</p>
                      )}
                    </div>
                  </>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                    <div className="flex items-center text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      {error}
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  size="lg"
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Join {familyInfo.familyName}
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center justify-center gap-1"
                  onClick={() => {
                    setStep(1);
                    setError('');
                    setFieldErrors({});
                  }}
                  disabled={loading}
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Change invite code
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};